<script>
    import { onMount } from "svelte";
    import { User, Language } from "@Store";
    import { to } from "@API";
    import Auth from "@components/Auth.svelte";
    import { push } from 'svelte-spa-router';

    let policies = null;
    let loading = false;

    onMount(() => {
        if ($User) {
            loadPolicies();
        }
    });

    async function loadPolicies() {
        loading = true;
        let [error, response] = await to(
            Insurecert.API.registration().policies.get()
        );

        if (response) {
            console.log("Policies loaded:", response);
            policies = response;
        }

        if (error) {
            console.error("Error loading policies:", error);
        }

        loading = false;
    }

    function onLoginSuccess() {
        console.log('User logged in successfully');
        loadPolicies();
    }

    function viewPolicy(policy) {
        push(`/${policy.uuid}/complete`);
    }

</script>

<div class="dashboard-container">
    <h1>{$Language === 'en' ? 'Dashboard' : 'Tableau de bord'}</h1>

    {#if !$User}
        <div class="auth-wrapper">
            <h2>{$Language === 'en' ? 'Login to view your policies' : 'Connectez-vous pour voir vos politiques'}</h2>
            <Auth onLoginSuccess={onLoginSuccess} defaultView="login" />
        </div>
    {:else}
        <h2>{$Language === 'en' ? 'Welcome, ' : 'Bienvenue, '}{$User.email}!</h2>

        {#if loading}
            <p>{$Language === 'en' ? 'Loading policies...' : 'Chargement des politiques...'}</p>
        {:else if policies && policies.length > 0}
            <div class="policies-list">
                <h3>{$Language === 'en' ? 'Your Policies' : 'Vos politiques'}</h3>
                <table>
                    <thead>
                        <tr>
                            <th>{$Language === 'en' ? 'Policy Number' : 'Numéro de politique'}</th>
                            <th>{$Language === 'en' ? 'Start Date' : 'Date de début'}</th>
                            <th>{$Language === 'en' ? 'End Date' : 'Date de fin'}</th>
                            <th>{$Language === 'en' ? 'Status' : 'Statut'}</th>
                            <th>{$Language === 'en' ? 'Action' : 'Action'}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {#each policies as policy}
                            <tr>
                                <td data-label="Policy Number">{policy.policy_number || policy.uuid}</td>
                                <td data-label="Start Date">{policy.start_date || '-'}</td>
                                <td data-label="End Date">{policy.end_date || '-'}</td>
                                <td data-label="Status">{policy.status || '-'}</td>
                                <td data-label="Action">
                                    <button on:click={() => viewPolicy(policy)}>
                                        {$Language === 'en' ? 'View Policy' : 'Voir la politique'}
                                    </button>
                                </td>
                            </tr>
                        {/each}
                    </tbody>
                </table>
            </div>
        {:else}
            <div class="no-policies">
                <p>{$Language === 'en' ? 'You don\'t have any policies yet.' : 'Vous n\'avez pas de politiques encore.'}</p>
            </div>
        {/if}
    {/if}
</div>

<style>
    .dashboard-container {
        max-width: 1200px;
        margin: 0 auto;
        padding: 2rem 1rem 3rem;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
    }

    h1 {
        text-align: center;
        margin: 0 0 0.5rem 0;
        font-size: 2rem;
        font-weight: 700;
        color: #1d1d1f;
        letter-spacing: -0.02em;
    }

    h2 {
        text-align: center;
        font-size: 1.5rem;
        font-weight: 600;
        color: #1d1d1f;
        margin: 2rem 0 1.5rem;
        letter-spacing: -0.02em;
    }

    .auth-wrapper {
        max-width: 500px;
        margin: 2rem auto;
    }

    .auth-wrapper h2 {
        margin-bottom: 1.5rem;
        font-size: 1.25rem;
        color: #86868b;
        font-weight: 500;
    }

    .policies-list {
        margin-top: 2rem;
    }

    .policies-list h3 {
        font-size: 1.25rem;
        font-weight: 600;
        color: #1d1d1f;
        margin-bottom: 1.5rem;
        letter-spacing: -0.01em;
    }

    table {
        width: 100%;
        border-collapse: separate;
        border-spacing: 0;
        background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
        border: 2px solid #e9ecef;
        border-radius: 20px;
        overflow: hidden;
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04);
    }

    thead {
        background: linear-gradient(135deg, #f0f9f1 0%, #f8fdf8 100%);
        border-bottom: 2px solid #e9ecef;
    }

    th {
        padding: 1.25rem 1.5rem;
        text-align: left;
        font-weight: 600;
        font-size: 0.9375rem;
        color: #1d1d1f;
        letter-spacing: -0.01em;
        border-bottom: 2px solid #e9ecef;
    }

    td {
        padding: 1.25rem 1.5rem;
        border-bottom: 1px solid #f0f0f0;
        color: #1d1d1f;
        font-size: 0.9375rem;
    }

    tbody tr {
        transition: all 0.2s ease;
    }

    tbody tr:hover {
        background: linear-gradient(135deg, #f5fdf6 0%, #ffffff 100%);
    }

    tbody tr:last-child td {
        border-bottom: none;
    }

    button {
        padding: 0.625rem 1.25rem;
        background: linear-gradient(135deg, var(--base-green) 0%, var(--base-green-light) 100%);
        color: white;
        border: 2px solid var(--base-green);
        border-radius: 12px;
        cursor: pointer;
        font-size: 0.9375rem;
        font-weight: 600;
        letter-spacing: -0.01em;
        transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
        box-shadow: 0 4px 12px rgba(49, 104, 50, 0.15);
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
    }

    button:hover {
        background: linear-gradient(135deg, var(--base-green-light) 0%, var(--base-green-lightest) 100%);
        transform: translateY(-2px);
        box-shadow: 0 8px 20px rgba(49, 104, 50, 0.25);
    }

    button:active {
        transform: translateY(0);
    }

    .no-policies {
        text-align: center;
        padding: 3rem 2rem;
        background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
        border: 2px solid #e9ecef;
        border-radius: 20px;
        margin-top: 2rem;
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04);
    }

    .no-policies p {
        color: #86868b;
        font-size: 1.125rem;
        margin: 0;
        font-weight: 500;
    }

    /* Responsive Design */
    @media (max-width: 768px) {
        .dashboard-container {
            padding: 1.5rem 0.75rem 2rem;
        }

        h1 {
            font-size: 1.75rem;
        }

        h2 {
            font-size: 1.25rem;
        }

        table {
            border-radius: 16px;
            font-size: 0.875rem;
        }

        th, td {
            padding: 1rem;
            font-size: 0.875rem;
        }

        button {
            padding: 0.5rem 1rem;
            font-size: 0.875rem;
        }

        /* Stack table on mobile */
        thead {
            display: none;
        }

        tbody tr {
            display: block;
            margin-bottom: 1rem;
            border: 2px solid #e9ecef;
            border-radius: 16px;
            overflow: hidden;
        }

        tbody tr:hover {
            background: #fff;
        }

        td {
            display: flex;
            justify-content: space-between;
            padding: 0.875rem 1rem;
            border-bottom: 1px solid #f0f0f0;
        }

        td:last-child {
            border-bottom: none;
        }

        td::before {
            content: attr(data-label);
            font-weight: 600;
            color: #6b7280;
            margin-right: 1rem;
        }
    }
</style>